import type { RouterType } from "@services/api";
import { QueryClient } from "@tanstack/react-query";
import { hc } from "hono/client";
import type { ClientResponse } from "hono/client";
import type { ContentfulStatusCode } from "hono/utils/http-status";
import type { MediaCategorySearchParams } from "~/features/category/types/MediaCategorySearchParams";
import type { OrganizationCategorySearchParams } from "~/features/category/types/OrganizationCategorySearchParams";
import type { ExampleSearchParams } from "~/features/search/types/ExampleSearchParams";
import type { GaijiSearchParams } from "~/features/search/types/GaijiSearchParams";
import type { ItemSearchParams } from "~/features/search/types/ItemSearchParams";
import type { MultimediaSearchParams } from "~/features/search/types/MultimediaSearchParams";
import type { SourceSearchParams } from "~/features/search/types/SourceSearchParams";
import { auth0Service } from "~/services/auth0";

export const createApiClient = async () =>
    hc<RouterType>("", {
        headers: {
            Authorization: `Bearer ${await auth0Service.getToken()}`,
        },
    }).api;

export class BadRequestError extends Error {
    code: string;
    requestId: string;

    constructor(code: string, message: string, requestId: string) {
        super(message);

        this.name = "BadRequestError";
        this.code = code;
        this.requestId = requestId;
    }
}

// 401 Unauthorized: 認証切れ・未認証。再ログインを促す。
export class UnauthorizedError extends Error {
    code: string;
    requestId: string;

    constructor(code: string, message: string, requestId: string) {
        super(message);

        this.name = "UnauthorizedError";
        this.code = code;
        this.requestId = requestId;
    }
}

// 403 Forbidden: 権限不足。回復不能なサーバー障害と区別して伝える。
export class ForbiddenError extends Error {
    code: string;
    requestId: string;

    constructor(code: string, message: string, requestId: string) {
        super(message);

        this.name = "ForbiddenError";
        this.code = code;
        this.requestId = requestId;
    }
}

// 403 + code USER_DELETED: 論理削除されたユーザーからのリクエスト (docs/design/user-deletion.md §4.5)。
// 認証は成功している (再ログインしても直らない) ので UnauthorizedError とは別画面で「削除されている」と伝える。
export class UserDeletedError extends Error {
    code: string;
    requestId: string;

    constructor(code: string, message: string, requestId: string) {
        super(message);

        this.name = "UserDeletedError";
        this.code = code;
        this.requestId = requestId;
    }
}

// 404 Not Found: 対象データが存在しない。
export class NotFoundError extends Error {
    code: string;
    requestId: string;

    constructor(code: string, message: string, requestId: string) {
        super(message);

        this.name = "NotFoundError";
        this.code = code;
        this.requestId = requestId;
    }
}

export class InternalServerError extends Error {
    code: string;
    requestId: string;

    constructor(code: string, message: string, requestId: string) {
        super(message);

        this.name = "InternalServerError";
        this.code = code;
        this.requestId = requestId;
    }
}

// #1655: 編集競合 (409 Conflict)。 別ユーザーの編集等、 回復可能な競合をサーバー障害と区別して伝える。
export class ConflictError extends Error {
    code: string;
    requestId: string;

    constructor(code: string, message: string, requestId: string) {
        super(message);

        this.name = "ConflictError";
        this.code = code;
        this.requestId = requestId;
    }
}

// #1655: 編集完了ロック等 (423 Locked)。 回復可能な競合としてサーバー障害と区別して伝える。
export class LockedError extends Error {
    code: string;
    requestId: string;

    constructor(code: string, message: string, requestId: string) {
        super(message);

        this.name = "LockedError";
        this.code = code;
        this.requestId = requestId;
    }
}

export type ErrorResponse = {
    code: string;
    message: string;
    requestId: string;
};

// status code → 対応する API エラークラスを生成する。 各クラスは code / message に加え requestId を保持するため、
// 呼び出し側 (ErrorBoundary 等) は instanceof で表示を出し分けつつ requestId を取り出せる。
// handleApiResponse と、 response.json() 済みのエラーを個別に投げる箇所 (editing-lock helpers 等) で共有する。
// 401/403/404 は内部エラーに潰さず専用 Error、 409/423 (#1655: 編集競合/ロック) も回復可能な競合として専用 Error。
export const createApiError = (status: number, error: ErrorResponse): Error => {
    switch (status) {
        case 400:
            return new BadRequestError(error.code, error.message, error.requestId);
        case 401:
            return new UnauthorizedError(error.code, error.message, error.requestId);
        case 403:
            return error.code === "USER_DELETED"
                ? new UserDeletedError(error.code, error.message, error.requestId)
                : new ForbiddenError(error.code, error.message, error.requestId);
        case 404:
            return new NotFoundError(error.code, error.message, error.requestId);
        case 409:
            return new ConflictError(error.code, error.message, error.requestId);
        case 423:
            return new LockedError(error.code, error.message, error.requestId);
        default:
            return new InternalServerError(error.code, error.message, error.requestId);
    }
};

export const handleApiResponse = async <T>(response: ClientResponse<T, ContentfulStatusCode, "json">): Promise<T> => {
    if (!response.ok) {
        // @see: https://github.com/honojs/hono/issues/2719
        // 上記issueが解決されれば as によるキャストは不要となるはず
        const error = (await response.json()) as unknown as ErrorResponse;
        throw createApiError(response.status, error);
    }

    return response.json();
};

// clientAction の保存失敗時に、サーバーのエラーメッセージ (ErrorResponse.message) を取り出す。
// throw せず submitStatus:"submitError" を返す action 用 (handleApiResponse は throw するため使えない)。
// body が空 / JSON でない / message を持たない場合は fallback を返す。
export const extractErrorMessage = async (
    response: { json: () => Promise<unknown> },
    fallback: string,
): Promise<string> => {
    const data = (await response.json().catch(() => null)) as { message?: string } | null;
    return data?.message ?? fallback;
};

// react-query のインスタンスを作成
export const queryClient = new QueryClient();

// react-query の queryKey を生成する関数
export const queryKey = {
    item: {
        list: (orgCode: string, mediaCode: string, searchParams: ItemSearchParams) => [
            "items",
            "list",
            orgCode,
            mediaCode,
            searchParams,
        ],
        preview: (orgCode: string, mediaCode: string, itemId: string) => [
            "items",
            "preview",
            orgCode,
            mediaCode,
            itemId,
        ],
        compareHistory: (orgCode: string, mediaCode: string, historyIds: string[]) => [
            "items",
            "compareHistory",
            orgCode,
            mediaCode,
            historyIds,
        ],
        exampleReferenceTimeline: (orgCode: string, mediaCode: string, itemHistoryId: string) => [
            "items",
            "exampleReferenceTimeline",
            orgCode,
            mediaCode,
            itemHistoryId,
        ],
        parentReferenceTimeline: (orgCode: string, mediaCode: string, itemHistoryId: string) => [
            "items",
            "parentReferenceTimeline",
            orgCode,
            mediaCode,
            itemHistoryId,
        ],
        compareHistoryBranch: (
            orgCode: string,
            mediaCode: string,
            itemHistoryId: string,
            exampleHistoryIds: string[],
        ) => ["items", "compareHistoryBranch", orgCode, mediaCode, itemHistoryId, exampleHistoryIds],
        compareHistoryParentBranch: (
            orgCode: string,
            mediaCode: string,
            itemHistoryId: string,
            parentItemHistoryIds: string[],
        ) => ["items", "compareHistoryParentBranch", orgCode, mediaCode, itemHistoryId, parentItemHistoryIds],
        view: (orgCode: string, mediaCode: string, originalId: string) => [
            "items",
            "view",
            orgCode,
            mediaCode,
            originalId,
        ],
        // 参照箇所ペイン用の content 取得。 視点 (最新の編集内容 / 編集完了時の編集内容) で
        // 叩く API が変わるため key に含める (既存の preview / view とは用途が別)。
        referenceContent: (orgCode: string, mediaCode: string, itemId: string, scope: string) => [
            "items",
            "referenceContent",
            orgCode,
            mediaCode,
            itemId,
            scope,
        ],
        children: (orgCode: string, mediaCode: string, itemId: string, page: number) => [
            "items",
            "children",
            orgCode,
            mediaCode,
            itemId,
            page,
        ],
    },
    example: {
        list: (orgCode: string, searchParams: ExampleSearchParams) => ["example", "list", orgCode, searchParams],
        preview: (orgCode: string, exampleId: string) => ["example", "preview", orgCode, exampleId],
        view: (orgCode: string, exampleId: string) => ["example", "view", orgCode, exampleId],
        ref: (orgCode: string, exampleId: string) => ["example", "linked", orgCode, exampleId],
        compareHistory: (orgCode: string, historyIds: string[]) => ["example", "compareHistory", orgCode, historyIds],
        sourceReferenceTimeline: (orgCode: string, exampleHistoryId: string) => [
            "example",
            "sourceReferenceTimeline",
            orgCode,
            exampleHistoryId,
        ],
        compareHistoryBranch: (orgCode: string, exampleHistoryId: string, sourceHistoryIds: string[]) => [
            "example",
            "compareHistoryBranch",
            orgCode,
            exampleHistoryId,
            sourceHistoryIds,
        ],
    },
    source: {
        list: (orgCode: string, searchParams: SourceSearchParams) => ["source", "list", orgCode, searchParams],
        preview: (orgCode: string, sourceId: string) => ["source", "preview", orgCode, sourceId],
        view: (orgCode: string, sourceId: string) => ["source", "view", orgCode, sourceId],
        ref: (orgCode: string, sourceId: string) => ["source", "linked", orgCode, sourceId],
        compareHistory: (orgCode: string, historyIds: string[]) => ["source", "compareHistory", orgCode, historyIds],
    },
    multimedia: {
        list: (orgCode: string, searchParams: MultimediaSearchParams) => ["multimedia", "list", orgCode, searchParams],
        get: (orgCode: string, multimediaId: string) => ["multimedia", "get", orgCode, multimediaId] as const,
        view: (orgCode: string, multimediaId: string) => ["multimedia", "view", orgCode, multimediaId],
        previewFilePaths: (orgCode: string, originalIds: readonly string[]) =>
            ["multimedia", "preview-file-paths", orgCode, originalIds] as const,
    },
    gaiji: {
        list: (orgCode: string, searchParams: GaijiSearchParams) => ["gaiji", "list", orgCode, searchParams],
        get: (orgCode: string, gaijiId: string) => ["gaiji", "get", orgCode, gaijiId] as const,
        previewResolution: (orgCode: string, entities: readonly string[], orgcodes: readonly string[]) =>
            ["gaiji", "preview-resolution", orgCode, entities, orgcodes] as const,
    },
    gaijiFile: {
        search: (orgCode: string, orgcode: string) => ["gaijiFile", "search", orgCode, orgcode] as const,
    },
    category: {
        organizationList: (orgCode: string, type?: string) => ["category", "organization", orgCode, type],
        organizationSearch: (orgCode: string, searchParams: OrganizationCategorySearchParams) => [
            "category",
            "organizationSearch",
            orgCode,
            searchParams,
        ],
        mediaList: (orgCode: string, mediaCode: string, type?: string) => [
            "category",
            "media",
            orgCode,
            mediaCode,
            type,
        ],
        mediaSearch: (orgCode: string, mediaCode: string, searchParams: MediaCategorySearchParams) => [
            "category",
            "mediaSearch",
            orgCode,
            mediaCode,
            searchParams,
        ],
    },
};
